import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Mail, Lock, User, Phone, ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, API } from '../App';

const CoachLoginPage = () => {
  const navigate = useNavigate();
  const { coachLogin } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' or 'request'
  const [loading, setLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [requestForm, setRequestForm] = useState({ 
    full_name: '', 
    email: '', 
    phone: '',
    message: '' 
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Use the context's coachLogin function which calls /auth/coach-login
      const userData = await coachLogin(loginForm.email, loginForm.password);
      
      if (!['coach', 'admin', 'superadmin'].includes(userData.role)) {
        toast.error('This login is for coaches only. Please use Client Login.');
        setLoading(false);
        return;
      }
      
      toast.success('Welcome back, Coach!');
      navigate('/coach');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await API.post('/coach/request-access', requestForm);
      toast.success('Access request sent! You will be contacted shortly.');
      setMode('login');
      setRequestForm({ full_name: '', email: '', phone: '', message: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send request';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&w=1200&q=80" 
          alt="Golf coaching"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2C5530]/80"></div>
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <GraduationCap className="w-16 h-16 mb-6" />
          <h1 className="font-['Playfair_Display'] text-4xl font-medium mb-4">
            Coach Portal
          </h1>
          <p className="text-white/80 text-lg leading-relaxed">
            Access your students' mental performance data, track their progress, 
            and help them achieve their goals on the course.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12">
        <div className="mx-auto w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#57534E] hover:text-[#2C5530] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="lg:hidden flex items-center gap-3 mb-8">
            <GraduationCap className="w-10 h-10 text-[#2C5530]" />
            <span className="font-['Playfair_Display'] text-2xl font-medium text-[#1A1A1A]">
              Coach Portal
            </span>
          </div>

          {mode === 'login' ? (
            <>
              <h2 className="font-['Playfair_Display'] text-3xl font-medium text-[#1A1A1A] mb-2">
                Coach Sign In
              </h2>
              <p className="text-[#57534E] mb-8">
                Access your coaching dashboard and student data
              </p>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                    <input
                      type="email"
                      name="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#2C5530]/20 focus:border-[#2C5530] bg-white"
                      placeholder="coach@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                    <input
                      type="password"
                      name="password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#2C5530]/20 focus:border-[#2C5530] bg-white"
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#2C5530] text-white py-3 font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors disabled:opacity-50"
                  data-testid="coach-login-submit"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>

                <div className="mt-3 text-right">
                  <Link
                    to="/forgot-password?type=coach"
                    className="text-sm text-[#737373] hover:text-[#2C5530]"
                    data-testid="coach-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </form>

              <div className="mt-8 pt-8 border-t border-[#E7E5E4]">
                <p className="text-[#57534E] text-center mb-4">
                  Don't have a coach account?
                </p>
                <button
                  onClick={() => setMode('request')}
                  className="w-full border-2 border-[#2C5530] text-[#2C5530] py-3 font-semibold uppercase tracking-wider hover:bg-[#2C5530] hover:text-white transition-colors"
                  data-testid="request-access-btn"
                >
                  Request Coach Access
                </button>
              </div>

              <p className="mt-6 text-center text-sm text-[#57534E]">
                Are you a client?{' '}
                <Link to="/login" className="text-[#2C5530] font-medium hover:underline">
                  Client Login
                </Link>
              </p>
            </>
          ) : (
            <>
              <h2 className="font-['Playfair_Display'] text-3xl font-medium text-[#1A1A1A] mb-2">
                Request Coach Access
              </h2>
              <p className="text-[#57534E] mb-8">
                Fill in your details and we'll set up your coach account
              </p>

              <form onSubmit={handleRequestAccess} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                    <input
                      type="text"
                      value={requestForm.full_name}
                      onChange={(e) => setRequestForm({ ...requestForm, full_name: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#2C5530]/20 focus:border-[#2C5530] bg-white"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                    <input
                      type="email"
                      value={requestForm.email}
                      onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#2C5530]/20 focus:border-[#2C5530] bg-white"
                      placeholder="coach@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                    <input
                      type="tel"
                      value={requestForm.phone}
                      onChange={(e) => setRequestForm({ ...requestForm, phone: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#2C5530]/20 focus:border-[#2C5530] bg-white"
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Message (Optional)
                  </label>
                  <textarea
                    value={requestForm.message}
                    onChange={(e) => setRequestForm({ ...requestForm, message: e.target.value })}
                    className="w-full px-4 py-3 border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#2C5530]/20 focus:border-[#2C5530] bg-white"
                    rows={3}
                    placeholder="Tell us about your coaching background..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#2C5530] text-white py-3 font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-request-btn"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Request
                    </>
                  )}
                </button>
              </form>

              <button
                onClick={() => setMode('login')}
                className="w-full mt-4 text-[#57534E] py-3 font-medium hover:text-[#2C5530] transition-colors"
              >
                ← Back to Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachLoginPage;
